import {
  fetchClinicNotificationCount,
  fetchClinicNotifications,
  handleMarkAllClinicNotificationsAsRead,
  handleUpdateClinicNotifications,
} from '@/entities/clinic';
import { CustomButton, useGlobalNotification } from '@/shared/ui/components';
import { useEmitter, useMergeState, useSetState, useUpdateEffect } from '@/shared/lib/customHooks';
import { usePhysicianStore } from '@/entities/physician';
import { useSupportStore } from '@/entities/support';
import { useUserStore } from '@/entities/user';
import { EMITTER_CONSTANTS } from '@/shared/config';
import { STUDY_STATUS } from '@/entities/study';
import {
  auth,
  emitter,
  GETTEXT_ALL_FACILITIES,
  showMessageError,
  showMessageInfo,
} from '@/shared/lib';
import type { NotificationSocketMessage } from '@/shared/lib';
import _ from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'ttag';
import { facilitySelection } from '@/entities/facility';
import {
  formatClinicNotifications,
  formatNotificationItem,
  formatNotificationList,
} from '../../lib/notificationCenter';
import { formatNotificationContent } from './notificationRenderers';
import type {
  ClinicNotificationItem,
  FormattedSupportFacility,
  InboxEventSocketMsg,
  NotificationInputItem,
  NotificationItem,
  PopupInfo,
  SelectedFacilityOption,
} from '../../lib/notificationCenter/notificationCenter.types';

/**
 * Drives the clinic notification center: badge count, drawer list, socket-pushed banners and the per-notification navigation callbacks. Builds the sorted facility options and the flagged-studies permission from the support store, paginates notifications via fetchData, debounces ShouldRefresh socket bursts through flushRefreshQueue, banners notification and inbox events, manages the drawer backdrop, scroll lock and an IntersectionObserver, and marks all read when the drawer toggles.
 *
 * @returns The drawer and badge state, the grouped notification list, the flagged-studies permission and the notification action callbacks.
 */
const useNotificationsCenter = () => {
  const location = useLocation();
  const notificationApi = useGlobalNotification();
  const navigate = useNavigate();
  /**
   * The list of support facilities from the support store. Read via a useSupportStore selector.
   *
   * @returns The support facilities array from the store.
   */
  const supportFacilities = useSupportStore((s) => s.supportFacilities);

  /**
   * Selects the changeSelectedFacility action from the user store. Read via a useUserStore selector.
   *
   * @returns The store's changeSelectedFacility action.
   */
  const changeSelectedFacilityRequestAction = useUserStore((s) => s.changeSelectedFacility);
  /**
   * Selects the fetchPhysicians action from the physician store. Read via a usePhysicianStore selector.
   *
   * @returns The store's fetchPhysicians action.
   */
  const fetchPhysiciansRequestAction = usePhysicianStore((s) => s.fetchPhysicians);

  const [state, setState] = useSetState({
    hasNotification: false,
    isLoadingMore: false,
    isLoadedData: false,
    isEndOfData: false,
    notifications: [] as ReturnType<typeof formatClinicNotifications>,
    visibleNoti: false,
  });

  const [notiState, setNotiState] = useMergeState({
    openPopover: false,
    count: 0,
  });

  const isPreventFetchMore = useRef(false);
  const refreshQueueCountRef = useRef(0);
  const refreshQueueTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isVisibleNotiRef = useRef(state.visibleNoti);

  /**
   * Computes the alphabetically sorted option list of support facilities for the facility selector. Maps supportFacilities to label/value/status/programs objects ordered ascending by label, and prepends an "All facilities" option when more than one facility exists.
   *
   * @returns The sorted facility options, optionally led by an All facilities entry.
   */
  const formattedSupportFacilities = useMemo(() => {
    const formattedSupportFacilitiesArr: FormattedSupportFacility[] = _.orderBy(
      _.map(supportFacilities, (supportFacility) => ({
        label: supportFacility.name,
        value: supportFacility.id,
        canViewFlaggedStudies: supportFacility.canViewFlaggedStudies,
        status: supportFacility.status,
        programs: supportFacility?.programs,
      })),
      ['label'],
      ['asc'],
    );
    if ((supportFacilities?.length ?? 0) > 1) {
      formattedSupportFacilitiesArr.unshift({
        label: GETTEXT_ALL_FACILITIES,
        value: 'All facilities',
      });
    }
    return formattedSupportFacilitiesArr;
  }, [supportFacilities]);

  /**
   * Whether any support facility permits viewing flagged studies. Computed by scanning supportFacilities for a canViewFlaggedStudies flag, memoized on that list.
   *
   * @returns True when at least one facility allows viewing flagged studies.
   */
  const hasFlaggedStudy = useMemo(
    () => _.some(supportFacilities, (supportFacility) => supportFacility.canViewFlaggedStudies),
    [supportFacilities],
  );

  /**
   * Whether the current user may view flagged studies. Computed as true when the user is a clinic technician or an admin/sales user and at least one facility has flagged studies (hasFlaggedStudy).
   *
   * @returns True when the user is permitted to view flagged studies.
   */
  const couldViewFlaggedStudies =
    (auth.isClinicTechnician() || auth.isAdminOrSales()) && hasFlaggedStudy;

  /**
   * Switches the active support facility when the user picks a different one. Skips when the option already matches the stored selection, otherwise emits a SHOW_POPUP_ESCALATION event with the matched facility's arrears, persists the option to localStorage, refetches physicians for that facility id (undefined for the all-facilities option) and dispatches the user store action.
   *
   * @param option - The facility option chosen by the user.
   * @returns Nothing.
   */
  const changeSelectedFacility = (option: SelectedFacilityOption) => {
    const selectedFacilityLocalStorage = facilitySelection.selectedFacilityLocalStorage();
    if (option.value !== selectedFacilityLocalStorage.value) {
      /**
       * Computes the support facility whose id matches the chosen option's value. Derived by scanning supportFacilities with an id-equality predicate.
       *
       * @returns The matching support facility, or undefined when none matches.
       */
      const foundSelectedFacility = _.find(supportFacilities, (x) => x.id === option.value);
      if (foundSelectedFacility) {
        emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_ESCALATION, foundSelectedFacility.arrears);
      }

      const selectedFacilityId = option.value !== GETTEXT_ALL_FACILITIES ? option.value : undefined;
      facilitySelection.setSelectedFacilityLocalStorage(option);
      fetchPhysiciansRequestAction(selectedFacilityId);
      changeSelectedFacilityRequestAction(option);
    }
  };

  /**
   * Prepends freshly arrived notifications to the top of the drawer list. Fetches the newest notifications up to limit, formats them and removes artifact studies that are aborted or lack a friendlyId, then merges them ahead of a deep clone of the current list de-duplicated by id; failures are only logged.
   *
   * @param limit - Maximum number of new notifications to fetch and prepend.
   * @returns Resolves once the new notifications have been merged into state.
   */
  const unshiftedNewNotifications = async (limit = 1) => {
    try {
      const fetchedNotifications = await fetchClinicNotifications({}, limit);
      const formattedNotifications = formatClinicNotifications(
        fetchedNotifications as ClinicNotificationItem[] | undefined,
      );
      _.forEach(formattedNotifications, (notification: any) => {
        _.remove(
          notification.artifactStudies,
          (x) => _.isNil(x.friendlyId) || x.status === STUDY_STATUS.ABORTED,
        );
      });
      if (formattedNotifications.length > 0) {
        const clonedNotifications = _.cloneDeep(state.notifications);
        const notifications = [...formattedNotifications, ...clonedNotifications];
        setState((prevState) => ({
          ...prevState,
          notifications: _.uniqBy(notifications, 'id'),
        }));
      }
    } catch (error) {
      console.error('Failed to fetch notifications: ', error);
    }
  };

  /**
   * Loads a page of clinic notifications into the drawer state. Fetches by cursor filter and limit, formats the results and drops artifact studies that are aborted or lack a friendlyId, then appends them when isFetchMore is set or replaces the list otherwise, shows a no-more-notifications info message on an empty fetch-more, invokes the callback, and surfaces failures as an error message.
   *
   * @param filter - Cursor filter narrowing which notifications are fetched.
   * @param limit - Maximum number of notifications to fetch.
   * @param callback - Invoked after the fetched notifications are stored.
   * @param isFetchMore - Whether the page is appended to the existing list rather than replacing it.
   * @returns Resolves once the fetched notifications have been stored.
   */
  const fetchData = async ({
    filter,
    limit,
    callback = () => {},
    isFetchMore = false,
  }: {
    filter: { cursor?: string };
    limit: number;
    callback?: () => void;
    isFetchMore?: boolean;
  }) => {
    try {
      const fetchedNotifications = await fetchClinicNotifications(filter, limit);

      const formattedNotifications = formatClinicNotifications(
        fetchedNotifications as ClinicNotificationItem[] | undefined,
      );
      _.forEach(formattedNotifications, (notification: any) => {
        _.remove(
          notification.artifactStudies,
          (x) => _.isNil(x.friendlyId) || x.status === STUDY_STATUS.ABORTED,
        );
      });

      setState({
        notifications: isFetchMore
          ? [...state.notifications, ...formattedNotifications]
          : formattedNotifications,
        isEndOfData: !formattedNotifications.length,
        isLoadedData: true,
        isLoadingMore: false,
      });

      if (!formattedNotifications.length && isFetchMore) {
        showMessageInfo({ content: t`No more notifications` });
      }

      if (callback) {
        callback();
      }
    } catch (error) {
      console.error('Failed to fetch notifications: ', error);
      showMessageError({ content: error instanceof Error ? error.message : String(error) });
    }
  };

  /**
   * Fetches the next page of notifications when the drawer is scrolled to its bottom. Bails out while a previous fetch-more is in flight or the end of data was reached, raises the loading-more flag, and calls fetchData with the createdAt of the last loaded notification as cursor and a limit of ten, clearing the guard from the callback.
   *
   * @returns Nothing.
   */
  const handleLoadMoreNotification = () => {
    if (isPreventFetchMore.current || state.isEndOfData) {
      return;
    }

    isPreventFetchMore.current = true;
    setState({ isLoadingMore: true });
    fetchData({
      filter: {
        cursor: state.notifications[state.notifications.length - 1].createdAt,
      },
      limit: 10,
      callback: () => {
        isPreventFetchMore.current = false;
      },
      isFetchMore: true,
    });
  };

  /**
   * Refreshes the unseen notification badge count. Fetches the count and stores unseen in noti state, and when isShowPopup is set and at least one notification is unseen opens the reminder popover then closes it again after five seconds; failures are only logged.
   *
   * @param isShowPopup - Whether to open the reminder popover when unseen notifications exist.
   * @returns Resolves once the count has been stored.
   */
  const handleFetchNotificationCount = async (isShowPopup?: boolean) => {
    try {
      const result = await fetchClinicNotificationCount();
      const { unseen } = result || {};
      setNotiState({ count: unseen ?? 0 });
      if ((unseen ?? 0) >= 1 && isShowPopup) {
        setNotiState({ openPopover: true });
        setTimeout(() => {
          setNotiState({ openPopover: false });
        }, 5000);
      }
    } catch (error) {
      console.log('Failed to fetch Notification Count', error);
    }
  };

  /**
   * Marks a single notification as read after the user acts on its popup. Returns immediately when the popup info does not request it, dismisses the open notification popups, then sends the notification id with isSeen true to the update call, logging failures.
   *
   * @param info - Popup metadata carrying the notification id and whether it should be marked as read.
   * @returns Resolves once the read state has been sent.
   */
  const handleMarkAsReadAction = async (info?: PopupInfo) => {
    const { shouldMarkAsRead, id } = info || {};
    if (!shouldMarkAsRead) return;
    notificationApi.destroy();
    try {
      await handleUpdateClinicNotifications({
        ids: id ? [id] : [],
        isSeen: true,
      });
    } catch (error) {
      console.log('Failed to mark as read:', error);
    }
  };

  /**
   * Clears the unseen notification badge by marking every clinic notification as read. Skips the call when the current count is already zero and logs failures without surfacing them.
   *
   * @returns Resolves once the mark-all call has completed.
   */
  const markAllAsReadNotifications = async () => {
    if (notiState.count <= 0) return;
    try {
      await handleMarkAllClinicNotificationsAsRead();
    } catch (error) {
      console.error('Failed to update notifications: ', error);
    }
  };

  /**
   * Opens the flagged studies tab from a notification or from the drawer header button. Closes the drawer, switches the facility selection to all facilities when it is currently scoped to one, marks the notification as read, and navigates to the studies route requesting the flagged-studies tab.
   *
   * @param info - Popup metadata used to mark the originating notification as read.
   * @returns Resolves once the read state has been sent and navigation was triggered.
   */
  const onClickNotification = async (info?: PopupInfo) => {
    setState({ visibleNoti: false });
    const selectedFacilityLocalStorage = facilitySelection.selectedFacilityLocalStorage();
    if (selectedFacilityLocalStorage.value !== GETTEXT_ALL_FACILITIES) {
      changeSelectedFacility({ value: GETTEXT_ALL_FACILITIES });
    }
    handleMarkAsReadAction(info);
    navigate('/studies', {
      state: { isFlaggedStudiesTab: true },
    });
  };

  /**
   * Opens an RMA at its outbound tracking section from a shipping-label notification. Closes the notification drawer, marks the notification as read, and navigates to the RMA detail route carrying an outboundTrackingInformation scroll target.
   *
   * @param rmaTicket - Ticket identifier of the RMA to open.
   * @param info - Popup metadata used to mark the originating notification as read.
   * @returns Nothing.
   */
  const onClickShippingLabel = (rmaTicket: string, info?: PopupInfo) => {
    setState({ visibleNoti: false });
    handleMarkAsReadAction(info);
    navigate(`/rma/${rmaTicket}`, {
      state: {
        idScroll: 'outboundTrackingInformation',
        hasScroll: false,
      },
    });
  };

  /**
   * Opens the maintenance tab of the fleet area for the facility a maintenance notification belongs to. Looks the item's facilityId up in the formatted facility options and, only when it is found, switches the selected facility, marks the notification as read, closes the drawer and navigates to the RMA route requesting the maintenance tab.
   *
   * @param item - The maintenance notification, whose facilityId selects the facility to activate.
   * @param info - Popup metadata used to mark the originating notification as read.
   * @returns Nothing.
   */
  const onClickViewMaintenance = (item: NotificationItem, info?: PopupInfo) => {
    /**
     * Computes the formatted facility option whose value matches the notification item's facilityId. Derived by scanning formattedSupportFacilities with a value-equality predicate.
     *
     * @returns The matching facility option, or undefined when none matches.
     */
    const foundFacility = _.find(formattedSupportFacilities, (x) => x.value === item.facilityId);
    if (foundFacility) {
      changeSelectedFacility(foundFacility);
      handleMarkAsReadAction(info);
      setState({ visibleNoti: false });
      navigate('/rma', {
        state: {
          isMaintenanceTab: true,
        },
      });
    }
  };

  /**
   * Bottom-scroll handler passed to the drawer's BottomScrollListener. Delegates straight to handleLoadMoreNotification.
   *
   * @returns Nothing.
   */
  const useBottomScrollListener = () => {
    handleLoadMoreNotification();
  };

  /**
   * Toggles the notification drawer between open and closed. Flips the visibleNoti flag of the local drawer state.
   *
   * @returns Nothing.
   */
  const toogleDrawer = () => {
    setState({ visibleNoti: !state.visibleNoti });
  };

  /**
   * Opens the detail page of the RMA referenced by a notification. Closes the notification drawer, marks the notification as read, and navigates to the RMA route of the given ticket.
   *
   * @param rmaTicket - Ticket identifier of the RMA to open.
   * @param info - Popup metadata used to mark the originating notification as read.
   * @returns Nothing.
   */
  const handleViewDetailsClick = (rmaTicket: string, info?: PopupInfo) => {
    setState({ visibleNoti: false });
    handleMarkAsReadAction(info);
    navigate(`/rma/${rmaTicket}`);
  };

  /**
   * Opens an RMA at its inbound tracking section from a notification's track-it-now action. Closes the notification drawer, marks the notification as read, and navigates to the RMA detail route carrying an inboundTrackingInformation scroll target.
   *
   * @param rmaTicket - Ticket identifier of the RMA to open.
   * @param info - Popup metadata used to mark the originating notification as read.
   * @returns Nothing.
   */
  const handleTrackItNowClick = (rmaTicket: string, info?: PopupInfo) => {
    setState({ visibleNoti: false });
    handleMarkAsReadAction(info);
    navigate(`/rma/${rmaTicket}`, {
      state: {
        idScroll: 'inboundTrackingInformation',
        hasScroll: false,
      },
    });
  };

  const callbackFuncs = {
    onClickShippingLabel,
    onClickViewMaintenance,
    handleViewDetailsClick,
    handleTrackItNowClick,
    onClickNotification,
  };

  /**
   * Shows a toast banner for a notification pushed over the socket. Normalises the message into a notification item, formats its title, content and facility name in popup mode with the current callbacks and flagged-studies permission, and opens a custom notification popup only when a title was produced, including the facility name when the user supports more than one facility.
   *
   * @param msg - The socket payload, whose notificationType becomes the item type.
   * @returns Nothing.
   */
  const handleShowBanner = (msg: NotificationInputItem) => {
    const item = formatNotificationItem({
      ...msg,
      type: msg.notificationType,
    });
    const { title, content, facilityName } = formatNotificationContent({
      item: item as NotificationItem,
      callbacks: callbackFuncs,
      isPopup: true,
      couldViewFlaggedStudies,
    });
    if (title) {
      notificationApi.open({
        message: title,
        description: (
          <div>
            {(supportFacilities?.length ?? 0) > 1 && (
              <div className="notification-facility-name">{facilityName}</div>
            )}
            {content}
          </div>
        ),
        className: 'custom-notification-popup',
      });
    }
  };

  /**
   * Applies the notifications queued by debounced ShouldRefresh socket messages. Resets the queued-count and timer refs, prepends that many notifications through unshiftedNewNotifications only while the drawer is visible, and always refreshes the unseen badge count.
   *
   * @returns Nothing.
   */
  const flushRefreshQueue = () => {
    const queuedCount = refreshQueueCountRef.current;
    if (queuedCount <= 0) {
      refreshQueueTimerRef.current = null;
      return;
    }
    refreshQueueCountRef.current = 0;
    refreshQueueTimerRef.current = null;
    if (isVisibleNotiRef.current) {
      unshiftedNewNotifications(queuedCount);
    }
    handleFetchNotificationCount();
  };

  /**
   * Queues a notification refresh requested by an incoming ShouldRefresh socket message. Increments the queued-count ref and schedules flushRefreshQueue one second later only when no timer is pending, so a burst of messages collapses into a single refresh.
   *
   * @returns Nothing.
   */
  const enqueueRefresh = () => {
    refreshQueueCountRef.current += 1;
    if (!refreshQueueTimerRef.current) {
      refreshQueueTimerRef.current = setTimeout(flushRefreshQueue, 1000);
    }
  };

  useEmitter(
    EMITTER_CONSTANTS.NOTIFICATION,
    (msg: NotificationSocketMessage) => {
      handleShowBanner(msg);
      // ShouldRefresh: this type occur by system, might be received multiple noti in a while => need to add into queue
      if (msg.type === 'ShouldRefresh') {
        enqueueRefresh();
      }
      // HaveBeenSeen: this type occur by user action so dont need to add into queue
      if (msg.type === 'HaveBeenSeen') {
        handleFetchNotificationCount();
      }
    },
    [state.notifications, supportFacilities, couldViewFlaggedStudies],
  );

  useEmitter(
    EMITTER_CONSTANTS.INBOX_EVENTS,
    (msg: InboxEventSocketMsg) => {
      if (!msg.recalled && !msg.inbox?.isReviewed && !msg.inbox?.isDownloaded) {
        if (auth.isClinicPhysician() && msg.physician !== auth.userId()) {
          return;
        }
        const reportTypeText =
          msg.type === 'Study'
            ? 'An End of Use'
            : msg.type === 'Notification'
              ? 'A Notification'
              : `An ${msg.type}`;
        const patientName = msg.patientName ? ` for ${msg.patientName}` : '';
        const content = `${reportTypeText} Report${patientName} is now available.`;
        const facilityName = msg.facility?.name || '';

        notificationApi.open({
          message: 'New report received',
          description: (
            <div>
              {(supportFacilities?.length ?? 0) > 1 && facilityName && (
                <div className="notification-facility-name">{facilityName}</div>
              )}
              <div className="notification-content">
                <div>{content}</div>
                <CustomButton
                  className="view-details-btn notification-content-btn"
                  type="primary"
                  size="small"
                  onClick={() => {
                    notificationApi.destroy();
                    navigate(`/inbox/${msg.id}`);
                  }}
                >
                  View
                </CustomButton>
              </div>
            </div>
          ),
          className: 'custom-notification-popup',
        });
      }
    },
    [supportFacilities],
  );

  useEffect(() => {
    /**
     * Computes an IntersectionObserver that unobserves each notification element once it becomes visible within the notification body container. Configured with that container as root and a 0.8 visibility threshold.
     *
     * @returns The configured intersection observer instance.
     */
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: document.getElementById('notification-body-container'),
        threshold: 0.8,
      },
    );
    const notificationItems = document.querySelectorAll('.notification');
    notificationItems.forEach((item) => {
      observer.observe(item);
    });
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [state.notifications]);

  useEffect(
    () => () => {
      if (refreshQueueTimerRef.current) {
        clearTimeout(refreshQueueTimerRef.current);
        refreshQueueTimerRef.current = null;
      }
      refreshQueueCountRef.current = 0;
    },
    [],
  );

  useEffect(() => {
    const appElements = document.getElementsByClassName('app');
    isVisibleNotiRef.current = state.visibleNoti;
    if (appElements?.length > 0) {
      if (state.visibleNoti) {
        const divElement = document.createElement('div');
        divElement.className = 'notification-popover-backdrop';
        appElements[0].appendChild(divElement);

        const notificationBodyContainerElement = document.getElementById(
          'notification-body-container',
        );
        if (notificationBodyContainerElement) {
          notificationBodyContainerElement.style.overflow = 'hidden';
        }

        setState({ isLoadedData: false });
        fetchData({
          filter: { cursor: undefined },
          limit: 10,
          callback: () => {
            if (notificationBodyContainerElement) {
              notificationBodyContainerElement.style.overflow = 'auto';
            }
          },
        });
        markAllAsReadNotifications();
      } else {
        const divElement = document.getElementsByClassName('notification-popover-backdrop');
        if (divElement?.length > 0) {
          appElements[0].removeChild(divElement[0]);
        }
        markAllAsReadNotifications();
      }
    }
  }, [state.visibleNoti]);

  useUpdateEffect(() => {
    setState({ visibleNoti: false });
  }, [location.pathname]);

  /**
   * Computes the display-ready notification list grouped for rendering. Returns an empty array when there are no notifications, otherwise formats state.notifications via formatNotificationList, memoized on that state.
   *
   * @returns The formatted notification list, or an empty array.
   */
  const notificationList = useMemo(() => {
    if (!state.notifications.length) {
      return [];
    }

    return formatNotificationList(state.notifications);
  }, [state.notifications]);

  useEffect(() => {
    handleFetchNotificationCount(true);
  }, []);

  return {
    state,
    notiState,
    setNotiState,
    couldViewFlaggedStudies,
    notificationList,
    callbackFuncs,
    onClickNotification,
    toogleDrawer,
    useBottomScrollListener,
  };
};

export { useNotificationsCenter };
